/** Rendered-body display core, extracted from ChannelResultCard so the docked
 *  live preview shares it: the sandboxed email iframe (preview) or the raw
 *  monospace block (plain text / non-email channels).
 *
 *  The rendered body is untrusted template output — HTML is displayed ONLY
 *  inside a fully sandboxed iframe (no scripts, no same-origin, no navigation).
 */
import { Box } from '@mui/material';

interface Props {
  body: string;
  /** True → the sandboxed HTML iframe; false → the raw text block. */
  preview: boolean;
  /** iframe title (accessibility). */
  title: string;
  /** Preview viewport width in px; 'full' fills the container. */
  width?: number | 'full';
  /** Simulate a dark-mode email client by inverting the preview colors. */
  dark?: boolean;
  height?: number | string;
}

export function RenderedBodyView({
  body,
  preview,
  title,
  width = 'full',
  dark = false,
  height = 480,
}: Props) {
  if (!preview) {
    return (
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 1.5,
          borderRadius: 1,
          bgcolor: 'action.hover',
          whiteSpace: 'pre-wrap',
          overflowX: 'auto',
          fontSize: 12,
          fontFamily: 'monospace',
        }}
      >
        {body}
      </Box>
    );
  }
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Box
        component="iframe"
        // Fully sandboxed: no scripts, no same-origin, no navigation — the
        // rendered email is untrusted template output.
        sandbox=""
        srcDoc={body}
        title={title}
        sx={{
          width: width === 'full' ? '100%' : width,
          maxWidth: '100%',
          height,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: '#fff',
          ...(dark ? { filter: 'invert(0.92) hue-rotate(180deg)' } : {}),
        }}
      />
    </Box>
  );
}
